import { Badge, Card, cn } from "@au-schein-de/au-ui-library";
import type { FormatedProductPrice } from "@dransay/api";
import { CATEGORIES, isDeviceKind } from "@dransay/api/models";
import { cva } from "class-variance-authority";
import { type JSX, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { HeartIcon } from "~/components/icons/heart-icon";
import { Price } from "~/components/price.component";
import { ProductImage } from "~/components/product-image.component";
import { PriceComparison } from "~/features/price-comparison/price-comparison.component";
import type { PriceComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { useFavorites } from "~/hooks/use-favorites.hook";
import { getImageSource } from "~/utils/default-image";
import { formatRatingCount } from "~/utils/format-rating-count.utils";
import { getThcCbdUnit } from "~/utils/product-unit.utils";
import { getProductDetailsUrl } from "~/utils/routes";
import { useCart } from "../../cart/cart.context";
import { getAmountInCartLabel } from "../../cart/cart.utils";
import {
	type BadgeType,
	getNewBadge,
	getProductBadges,
} from "./product-card-badges";
import { ProductCardCannabinoidBadgeRow } from "./product-card-cannabinoid-badge-row.component";
import { ProductCardEffects } from "./product-card-effects.component";
import { ProductCardRatingRow } from "./product-card-rating-row.component";
import { ProductCardTerpenes } from "./product-card-terpenes.component";

type Category = (typeof CATEGORIES)[keyof typeof CATEGORIES];

type ProductCardProps = FormatedProductPrice & {
	url?: string;
	// this is a quick workaround for tests to be able to
	// differentiate between products with the same id
	idSuffix?: string;
	// @todo we should query for default image for category
	category: Category | string | null;
	border?: "main" | "light" | "none";
	size?: "default" | "sm";
	variant?: "listItem" | "carouselItem" | "favoriteItem";
	listIndex: number;
	priceComparisonDiscount?: PriceComparisonDiscount;
	badges?: BadgeType[];
	forceExclusiveBadge?: boolean;
	exclusivePdpCard?: boolean;
	isFavoriteOverride?: boolean;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	productAvailable?: boolean;
	isOutOfStock?: boolean;
};

const productCardFooterVariant = cva("px-0 pb-1 pt-1", {
	variants: {
		size: {
			default: "md:pb-4 md:pt-3",
			sm: "",
		},
		layout: {
			default: "flex flex-wrap",
			carousel: "flex w-full flex-col items-start",
		},
	},
	defaultVariants: {
		layout: "default",
	},
});

const productCardHeaderVariant = cva(
	"truncate text-gray-700 text-body-bold mt-3 md:mt-3",
	{
		variants: {
			size: {
				default: "md:text-h4",
				sm: "text-gray-700",
			},
		},
	},
);

const CAROUSEL_CARD_HEIGHT_CLASS = "min-h-[305px] md:min-h-[325px]";

const productImageContainerVariant = cva(
	"relative product-image w-full flex justify-center overflow-hidden shrink-0",
	{
		variants: {
			promotion: {
				none: "h-36",
				visible: "h-[116px]",
			},
		},
		defaultVariants: {
			promotion: "none",
		},
	},
);

const productImageVariant = cva(
	"object-contain transition-transform duration-500 group-hover:scale-110",
	{
		variants: {
			promotion: {
				none: "w-32 h-32 md:h-36 md:w-36",
				visible: "h-[100px] w-[100px] md:h-[116px] md:w-[116px]",
			},
		},
		defaultVariants: {
			promotion: "none",
		},
	},
);

export function ProductCard({
	url,
	border = "none",
	idSuffix,
	variant = "listItem",
	size = "default",
	badges = [],
	forceExclusiveBadge = false,
	exclusivePdpCard = false,
	onClick,
	isFavoriteOverride,
	productAvailable = true,
	...product
}: ProductCardProps): JSX.Element {
	const {
		id,
		genetics,
		imageUrl,
		name,
		rating,
		ratingCount,
		thc,
		cbd,
		minPrice,
		terpenes = [],
		kind,
		category,
		priceComparisonDiscount,
	} = product;

	const { toggleFavorite } = useFavorites();

	const productUrl = url ?? getProductDetailsUrl(id, name, kind);

	const { t } = useTranslation();
	const { t: tProduct } = useTranslation("product");

	const { items } = useCart();
	const quantityInCart = items.find((item) => item.id === id)?.quantity;

	const navigate = useNavigate();

	const isOutOfStock = product.isOutOfStock;

	const goToProduct = async (event: React.MouseEvent<HTMLAnchorElement>) => {
		if (
			event.ctrlKey ||
			event.metaKey ||
			event.shiftKey ||
			event.button !== 0
		) {
			onClick?.(event);
			return;
		}

		event.preventDefault();

		onClick?.(event);

		navigate(productUrl, { state: { from: "/products" } });
	};

	const handleFavoriteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		toggleFavorite(id, name);
	};

	const badgeSize = useMemo(
		() => (variant === "carouselItem" ? "xs" : "default"),
		[variant],
	);

	const cardContainerVariant = useMemo(() => {
		if (variant === "favoriteItem") {
			return "md:h-[414px] md:w-[282px]";
		}
		return "";
	}, [variant]);

	const parseExpertRating = (
		rating: string | number | null | undefined,
	): number | null => {
		if (rating == null) return null;
		const parsed = Number(rating);
		return Number.isFinite(parsed) && parsed >= 1 && parsed <= 5
			? parsed
			: null;
	};
	const expertRating = parseExpertRating(product.expertReviewRating);
	const showExpertRating =
		product.showExpertReview === true && expertRating !== null;
	const showCannabinoidBadges = !(
		category === CATEGORIES.INHALATIONS_EXTRACT && isDeviceKind(kind)
	);
	const isCarouselItem = variant === "carouselItem";
	const thcCbdUnit = getThcCbdUnit(category);
	const thcFullLabel = `${t("THC")}: ${thc}${thcCbdUnit}`;
	const thcShortLabel = `${t("THCShort")}: ${thc}${thcCbdUnit}`;
	const cbdFullLabel = `${t("CBD")}: ${cbd}${thcCbdUnit}`;
	const cbdShortLabel = `${t("CBDShort")}: ${cbd}${thcCbdUnit}`;
	const hasVisiblePromotion =
		priceComparisonDiscount?.visible === true &&
		productAvailable &&
		!isOutOfStock;
	const imagePromotionState = hasVisiblePromotion ? "visible" : "none";
	const isFixedHeightCard =
		isCarouselItem || variant === "listItem" || variant === "favoriteItem";
	const footerLayout = isCarouselItem ? "carousel" : "default";
	const noRatingLabel = t("noRatingYet");
	const hasUserRating = Boolean(Number.parseFloat(String(rating)));

	return (
		<div
			className={cn(
				"notranslate block relative h-full",
				variant === "carouselItem" && [
					"pl-2 md:pl-3",
					CAROUSEL_CARD_HEIGHT_CLASS,
				],
				cardContainerVariant,
			)}
			translate="no"
		>
			<a
				href={productUrl}
				onClick={goToProduct}
				data-testid={`product-${id}${idSuffix ? `-${idSuffix}` : ""}`}
				className={cn(
					"block group",
					variant === "carouselItem"
						? cn("h-full", CAROUSEL_CARD_HEIGHT_CLASS)
						: "h-full",
				)}
			>
				<Card.Root
					className={cn(
						"group flex h-full min-h-0 flex-col",
						variant === "favoriteItem" && "rounded-2xl md:rounded-3xl",
					)}
					variant="listItem"
					border={quantityInCart ? "main" : border}
				>
					<Card.Header className="shrink-0 p-0">
						<div
							className={cn(
								productImageContainerVariant({
									promotion: imagePromotionState,
								}),
								(isOutOfStock || !productAvailable) && "grayscale",
							)}
						>
							<ProductImage
								src={getImageSource(imageUrl, category)}
								alt={name ?? ""}
								viewport="thumbnail"
								className={productImageVariant({
									promotion: imagePromotionState,
								})}
								loading={product.listIndex < 4 ? "eager" : "lazy"}
								fetchPriority={product.listIndex < 4 ? "high" : undefined}
							/>

							<div className="absolute top-0 left-0 w-full flex justify-between items-top">
								<ol className="p-0 m-0 leading-[0] gap-1 flex-col">
									{genetics && showCannabinoidBadges && (
										<li className="mb-1">
											<Badge size={badgeSize} className="capitalize">
												{genetics}
											</Badge>
										</li>
									)}
									{!isOutOfStock &&
										getProductBadges(product, badges, {
											forceExclusive: forceExclusiveBadge,
										})}
								</ol>

								{!!quantityInCart && (
									<div className="flex items-center justify-center w-10 h-10 bg-ansay-main text-white rounded-full">
										<span
											className={cn(
												"text-body-bold",
												(category === CATEGORIES.EDIBLES ||
													category === CATEGORIES.DRINKABLES ||
													category === CATEGORIES.CAPSULES) &&
													"text-xs",
											)}
										>
											{getAmountInCartLabel(t, quantityInCart, category, kind)}
										</span>
									</div>
								)}
							</div>

							{!isOutOfStock && (
								<div className="absolute bottom-0 left-0">
									{getNewBadge(product)}
								</div>
							)}

							<button
								type="button"
								onClick={handleFavoriteClick}
								className="absolute bottom-0 right-0 p-2.5 rounded-full bg-gray-100 cursor-pointer group"
								data-testid={`favorite-button-${id}`}
								data-state={isFavoriteOverride ? "active" : "inactive"}
							>
								<HeartIcon
									filled={isFavoriteOverride}
									className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors"
								/>
							</button>
						</div>
					</Card.Header>
					<Card.Description
						className={cn(
							isCarouselItem && "flex min-w-0 flex-col",
							isFixedHeightCard && "min-h-0 flex-1",
						)}
					>
						<div
							className={cn(
								"hidden md:flex min-w-0 w-full gap-1 items-start",
								isCarouselItem ? "mt-1 min-h-6" : "mt-3 min-h-8",
							)}
						>
							{showCannabinoidBadges && (
								<ProductCardCannabinoidBadgeRow
									thcFullLabel={thcFullLabel}
									thcShortLabel={thcShortLabel}
									cbdFullLabel={cbdFullLabel}
									cbdShortLabel={cbdShortLabel}
									size={badgeSize}
									compactPadding={isCarouselItem}
								/>
							)}
						</div>

						<div className={cn(isCarouselItem && "w-full min-w-0 shrink-0")}>
							<div
								className={cn(
									productCardHeaderVariant({ size }),
									isCarouselItem && "mt-1 min-w-0",
									"transition-colors duration-500 group-hover:text-ansay-main",
								)}
								title={name}
							>
								{name}
							</div>
							<div
								data-testid="product-list-item-kind"
								className={cn(
									"text-body-sm mt-1 min-w-0 truncate",
									exclusivePdpCard ? "text-gray-700" : "text-gray-500",
								)}
								title={kind ?? undefined}
							>
								{kind}
							</div>
						</div>

						<ProductCardRatingRow
							size={size}
							rating={rating}
							ratingCount={formatRatingCount(ratingCount)}
							noRatingLabel={noRatingLabel}
							hasUserRating={hasUserRating}
							showExpertRating={showExpertRating}
							expertRating={expertRating ?? 0}
						/>

						{(variant === "listItem" || variant === "favoriteItem") && (
							<div className="hidden md:flex gap-3 mt-3 min-h-5">
								{showCannabinoidBadges && (
									<>
										<ProductCardEffects product={product} />
										<ProductCardTerpenes terpenes={terpenes} />
									</>
								)}
							</div>
						)}

						<div className="flex md:hidden min-w-0 w-full gap-1 col-span-4 mt-1 min-h-6 items-start">
							{showCannabinoidBadges && (
								<ProductCardCannabinoidBadgeRow
									thcFullLabel={thcFullLabel}
									thcShortLabel={thcShortLabel}
									cbdFullLabel={cbdFullLabel}
									cbdShortLabel={cbdShortLabel}
									size="xs"
									compactPadding={isCarouselItem}
								/>
							)}
						</div>
					</Card.Description>
					<Card.Footer
						className={cn(
							productCardFooterVariant({ size, layout: footerLayout }),
							isFixedHeightCard && "mt-auto shrink-0",
						)}
					>
						{productAvailable && !isOutOfStock ? (
							<>
								<Price
									// TODO: find elegant solution for this
									className={cn(
										variant === "carouselItem" && "text-body-bold",
										variant === "carouselItem" &&
											(exclusivePdpCard ? "text-ansay-700" : "text-ansay-main"),
										variant === "carouselItem" &&
											size === "default" &&
											"text-h4",
									)}
									variant={variant === "carouselItem" ? "raw" : "responsive"}
									price={Number(minPrice)}
									withFromLabel
									category={category}
									kind={kind}
								/>
								<PriceComparison
									className="mt-1"
									priceComparisonDiscount={priceComparisonDiscount}
									category={category}
									kind={kind}
								/>
							</>
						) : (
							<div className="w-full bg-gray-100 text-gray-500 text-body-sm text-center py-3 px-4 rounded-lg">
								{tProduct("productNotAvailable")}
							</div>
						)}
					</Card.Footer>
				</Card.Root>
			</a>
		</div>
	);
}
